import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FiBookOpen, FiFileText, FiAward, FiInfo, FiArrowRight, FiCopy, FiCheck } from 'react-icons/fi';
import PublicationItem from '../components/PublicationItem';
import SEO from '../components/SEO';
import '../styles/how-to-cite.css';

const HowToCitePage = () => {
    const [citationData, setCitationData] = useState({ categories: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [copiedId, setCopiedId] = useState(null);

    useEffect(() => {
        fetch('/citations.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch citations data');
                }
                return response.json();
            })
            .then(data => {
                setCitationData(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching citations:", err);
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const scrollToSection = (e, id) => {
        e.preventDefault();
        const element = document.getElementById(id);
        if (element) {
            const offset = 100;
            const bodyRect = document.body.getBoundingClientRect().top;
            const elementRect = element.getBoundingClientRect().top;
            const elementPosition = elementRect - bodyRect;
            const offsetPosition = elementPosition - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    const getIcon = (id) => {
        switch (id) {
            case 'main-citations': return <FiAward />;
            case 'specific-apps': return <FiBookOpen />;
            case 'datasets': return <FiFileText />;
            default: return <FiInfo />;
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-5 text-center">
                <div className="alert alert-danger d-inline-block">
                    Error loading citations: {error}
                </div>
            </Container>
        );
    }

    return (
        <div className="how-to-cite-container py-5">
            <SEO
                title="How to Cite"
                description="Attribution guidelines for referencing the platform and datasets in scientific publications."
            />

            {/* Header Section */}
            <header className="text-center mb-5 bento-header">
                <h1 className="display-4 font-weight-bold">How to Cite</h1>
                <p className="lead text-muted cite-intro-bento">
                    Properly attribute the platform and datasets to support open science and reproducible research.
                </p>
            </header>

            <Row className="g-4">
                {/* Sidebar Table of Contents */}
                <Col lg={3} className="d-none d-lg-block">
                    <div className="cite-sidebar">
                        <span className="tile-label mb-3 d-block">Contents</span>
                        <div className="d-flex flex-column gap-1">
                            {citationData.categories.map((category) => (
                                <a
                                    key={category.id}
                                    href={`#${category.id}`}
                                    onClick={(e) => scrollToSection(e, category.id)}
                                    className="toc-link"
                                >
                                    {category.title}
                                </a>
                            ))}
                        </div>
                    </div>
                </Col>

                {/* Main Content */}
                <Col lg={9}>
                    <div className="bento-grid-cite">
                        {/* License Tile */}
                        <div className="cite-tile tile-license mb-4">
                            <div className="tile-icon-bg"><FiInfo /></div>
                            <div className="tile-content">
                                <span className="cite-label">License & Terms</span>
                                <h2 className="cite-title-main">Open Access Commitment</h2>
                                <p className="cite-desc">
                                    By using Content from electrospinning-data.org, you agree to the <a href='https://creativecommons.org/licenses/by/4.0/' target='_blank' rel='noopener noreferrer' className="fw-bold">Creative Commons Attribution 4.0 License</a>.
                                    This means you are free to copy, distribute, and adapt the work, provided proper attribution is given.
                                </p>
                            </div>
                        </div>

                        {/* Citation Categories */}
                        {citationData.categories.map((category) => (
                            <div key={category.id} id={category.id} className="cite-tile tile-category mb-4">
                                <div className="tile-icon-bg">{getIcon(category.id)}</div>
                                <div className="tile-content">
                                    <div className="category-header">
                                        <span className="cite-label">{category.id === 'main-citations' ? 'Primary Reference' : 'Category'}</span>
                                        <h2 className="cite-title-main">{category.title}</h2>
                                        {category.description && (
                                            <div
                                                className="cite-desc mb-3"
                                                dangerouslySetInnerHTML={{ __html: category.description }}
                                            />
                                        )}
                                        {category.id === 'main-citations' && (
                                            <p className="cite-desc text-muted small">
                                                If you use electrospinning-data.org as a resource in your research, please cite the following work:
                                            </p>
                                        )}
                                    </div>

                                    <div className="citations-list">
                                        {category.citations.map((citation) => (
                                            <div key={citation.id} className="citation-item">
                                                <PublicationItem publication={citation} />
                                                {citation.description && (
                                                    <p className="text-muted small mt-2 mb-0 border-top pt-2">
                                                        {citation.description}
                                                    </p>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default HowToCitePage;
